import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Inbox, LogOut, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { adminApi } from "@/lib/adminApi";
import { SITE } from "@/content/site";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAdminAuth } from "@/components/admin/AdminAuth";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { CheckCard, ContactCard } from "@/components/admin/SubmissionCards";
import { Wordmark } from "@/components/site/Wordmark";
import { ErrorAlert } from "@/components/forms/FormBits";

const META = { title: "Founders Inbox | ATA Forge", description: "Private inbox for ATA Forge form submissions." };

const TABS = [
  { id: "contacts", label: "Contact messages", empty: "No contact messages yet." },
  { id: "website_checks", label: "Website checks", empty: "No website check requests yet." },
];

const Header = () => {
  const { user, logout } = useAdminAuth();
  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.08] bg-[#0A0B0D]/85 backdrop-blur-xl">
      <div className="container-x flex h-16 items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Wordmark testId="admin-wordmark" />
          <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1AA] sm:inline">Inbox</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden text-sm text-[#A1A1AA] md:inline" data-testid="admin-user-email">
            {user?.email}
          </span>
          <ChangePasswordDialog />
          <button
            type="button"
            onClick={logout}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 px-3.5 text-sm text-white/85 transition-colors hover:bg-white/[0.06]"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default function AdminInbox() {
  usePageMeta(META);
  const { setUser } = useAdminAuth();
  const [tab, setTab] = useState("contacts");
  const [data, setData] = useState({ contacts: [], website_checks: [] });
  const [status, setStatus] = useState("loading");

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const { data: res } = await adminApi.get("/admin/submissions");
      setData({
        contacts: Array.isArray(res?.contacts) ? res.contacts : [],
        website_checks: Array.isArray(res?.website_checks) ? res.website_checks : [],
      });
      setStatus("ready");
    } catch (err) {
      if (err?.response?.status === 401) setUser(false);
      else setStatus("error");
    }
  }, [setUser]);

  useEffect(() => {
    load();
  }, [load]);

  const active = TABS.find((t) => t.id === tab);
  const items = data[tab];
  const Card = tab === "contacts" ? ContactCard : CheckCard;

  return (
    <>
      <Header />
      <main className="container-x py-12 sm:py-16">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow">Founders only</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-[-0.035em] sm:text-4xl" data-testid="admin-inbox-title">
              Submissions
            </h1>
            <p className="mt-2 text-[15px] text-[#A1A1AA]">
              Newest first. Every request is also emailed to {SITE.email}.
            </p>
          </div>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white" data-testid="admin-view-site-link">
            View site <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.08] pb-4">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Submission type">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
                  tab === t.id ? "border-[#2F6BFF]/60 bg-[#2F6BFF]/15 text-white" : "border-white/10 text-[#A1A1AA] hover:text-white",
                )}
              >
                {t.label}
                <span className="rounded-full bg-white/10 px-2 py-0.5 font-mono text-xs" data-testid={`admin-count-${t.id}`}>
                  {data[t.id].length}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={status === "loading"}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 px-3.5 text-sm text-white/85 transition-colors hover:bg-white/[0.06] disabled:opacity-60"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={cn("h-4 w-4", status === "loading" && "animate-spin")} aria-hidden="true" /> Refresh
          </button>
        </div>

        <section className="mt-8 space-y-4" role="tabpanel" aria-label={active.label} data-testid="admin-submissions-list">
          {status === "error" && (
            <ErrorAlert message="We couldn't load submissions. Check your connection and press Refresh." testId="admin-load-error" />
          )}
          {status === "loading" && items.length === 0 && (
            <div className="space-y-4" aria-hidden="true">
              {[0, 1].map((i) => (
                <div key={i} className="h-40 animate-pulse rounded-2xl border border-white/[0.06] bg-white/[0.02]" />
              ))}
            </div>
          )}
          {status === "ready" && items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 px-6 py-16 text-center" data-testid="admin-empty-state">
              <Inbox className="mx-auto h-8 w-8 text-[#A1A1AA]" aria-hidden="true" />
              <p className="mt-4 text-lg font-medium">{active.empty}</p>
              <p className="mt-1 text-sm text-[#A1A1AA]">New submissions from the website will show up here.</p>
            </div>
          )}
          {items.map((item) => (
            <Card key={item.id} item={item} />
          ))}
        </section>
      </main>
    </>
  );
}
